import { html, type TemplateResult } from 'lit';
import type { WebsiteComponentDefinition, WebsiteComponentRenderContext } from './types.js';

export const WEBSITE_BUILDER_IMAGE_VIEWER_ENABLED_KEY = 'imageViewerEnabled';

const IMAGE_VIEWER_STYLES = `
  .website-builder-image-viewer {
    position: relative;
    min-width: 0;
  }
  .website-builder-image-viewer img:not([aria-hidden='true']) {
    cursor: zoom-in;
  }
  .website-builder-image-viewer__trigger {
    position: absolute;
    right: 12px;
    bottom: 12px;
    z-index: 2;
    width: 42px;
    height: 42px;
    display: grid;
    place-items: center;
    border: 0;
    border-radius: 999px;
    padding: 0;
    color: #070713;
    background: rgb(255 255 255 / 0.94);
    box-shadow: 0 2px 8px rgb(7 7 19 / 0.18);
    cursor: zoom-in;
    transition: transform 180ms cubic-bezier(0.22, 1, 0.36, 1), background-color 180ms ease;
  }
  .website-builder-image-viewer__trigger:hover {
    background: #ffffff;
    transform: scale(1.05);
  }
  .website-builder-image-viewer__trigger:focus-visible,
  .website-builder-image-viewer:focus-visible {
    outline: 3px solid #f14261;
    outline-offset: 3px;
  }
  .website-builder-image-viewer__trigger .material-symbols-outlined {
    font-size: 22px;
  }
  @media (max-width: 520px) {
    .website-builder-image-viewer__trigger {
      right: 8px;
      bottom: 8px;
      width: 44px;
      height: 44px;
    }
  }
  @media (prefers-reduced-motion: reduce) {
    .website-builder-image-viewer__trigger {
      transition: none;
    }
  }
`;

const openImageViewer = async (
  container: HTMLElement,
  preferredImage: HTMLImageElement | undefined,
  fallback: { src?: string; alt?: string },
) => {
  const image =
    preferredImage ??
    [...container.querySelectorAll<HTMLImageElement>('img:not([aria-hidden="true"])')].find(candidate =>
      Boolean(candidate.currentSrc || candidate.src),
    );
  const src = image ? image.currentSrc || image.src : fallback.src;
  if (!src) return;
  const { showWebsiteBuilderImageViewer } = await import('./client/image-viewer-dialog.js');
  await showWebsiteBuilderImageViewer({
    src,
    alt: image?.alt.trim() || fallback.alt || 'Vergrößerte Bildansicht',
  });
};

export const renderWebsiteBuilderImageViewer = (options: {
  content: unknown;
  label: string;
  mode: WebsiteComponentRenderContext['mode'];
  source?: string;
  alt?: string;
}): TemplateResult => {
  const activate = (event: MouseEvent) => {
    const container = event.currentTarget as HTMLElement;
    const target = event.target as Element | null;
    if (!target || typeof target.closest !== 'function') return;
    const trigger = target.closest('[data-website-image-viewer-trigger]');
    const image = target.closest('img:not([aria-hidden="true"])');
    if (!trigger && (!image || options.mode === 'editor')) return;
    event.preventDefault();
    event.stopPropagation();
    void openImageViewer(container, image?.tagName === 'IMG' ? (image as HTMLImageElement) : undefined, {
      src: options.source,
      alt: options.alt,
    });
  };
  return html`
    <style>
      ${IMAGE_VIEWER_STYLES}
    </style>
    <div
      class="website-builder-image-viewer"
      data-website-image-viewer
      data-website-image-viewer-mode=${options.mode}
      @click=${activate}
    >
      ${options.content}
      <button
        class="website-builder-image-viewer__trigger"
        data-website-image-viewer-trigger
        type="button"
        contenteditable="false"
        aria-label="${options.label} vergrößern"
        title="Bild vergrößern"
      >
        <span class="material-symbols-outlined" aria-hidden="true">zoom_in</span>
      </button>
    </div>
  `;
};

export const renderWebsiteComponentWithImageViewer = (
  definition: WebsiteComponentDefinition,
  context: WebsiteComponentRenderContext,
): TemplateResult => {
  const content = definition.render(context);
  const configured = context.attrs.props[WEBSITE_BUILDER_IMAGE_VIEWER_ENABLED_KEY];
  const enabled = typeof configured === 'boolean' ? configured : Boolean(definition.imageViewer?.enabledByDefault);
  const imageField = definition.inspector?.find(field => field.kind === 'image');
  if (!enabled || !imageField) return content;
  const source = readInspectorValue(context, imageField.target, imageField.key);
  const altField = definition.inspector?.find(
    field =>
      field.target === 'props' && field.kind === 'text' && /(^|\.)(alt|imageAlt|alternativeText)$/i.test(field.key),
  );
  const alt = altField ? readInspectorValue(context, altField.target, altField.key) : undefined;
  return renderWebsiteBuilderImageViewer({
    content,
    label: definition.label,
    mode: context.mode,
    source,
    alt,
  });
};

const readInspectorValue = (
  context: WebsiteComponentRenderContext,
  target: 'props' | 'styles' | 'responsive',
  key: string,
): string | undefined => {
  let value: unknown = context.attrs[target];
  for (const segment of key.split('.')) {
    if (!value || typeof value !== 'object' || Array.isArray(value)) return undefined;
    value = (value as Record<string, unknown>)[segment];
  }
  return typeof value === 'string' && value.trim() ? value : undefined;
};
